/**
 * VariableEditDialog - Dialog for editing a variable
 * Shows textarea for editing value and checkbox for freezing
 */

import type { Component, ChangeEvent } from 'react';
import { Variable } from '../models/Variable';
import { DateTimes } from '../utils/DateTimes';
import Dialog from './Dialog';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

// Forward declarations
declare const cheatFreezes: {
    variables: { [key: string]: boolean };
};

interface VariableEditDialogProps {
    variable: Variable;
    onSave: (value: string, frozen: boolean) => void;
    onCancel: () => void;
}

interface VariableEditDialogState {
    value: string;
    frozen: boolean;
    arrayValues: string[];
}

class VariableEditDialog extends ReactComponent<VariableEditDialogProps, VariableEditDialogState> {
    private isArrayType(vartype: string): boolean {
        return vartype.includes('ARRAY');
    }

    private isNumberType(vartype: string): boolean {
        return vartype === 'VT_NUMBER' || vartype === 'VT_NUMBERARRAY';
    }

    private isDateTimeType(vartype: string): boolean {
        return vartype === 'VT_DATETIME' || vartype === 'VT_DATETIMEARRAY';
    }

    private toInputDateTime(value: string): string {
        if (!value) return '';
        const moment = (window as any).moment;
        if (moment) {
            const parsed = moment(value, DateTimes.defaultDateFormat, true);
            if (parsed.isValid()) {
                return parsed.format('YYYY-MM-DDTHH:mm');
            }
            const isoParsed = moment(value, moment.ISO_8601, true);
            if (isoParsed.isValid()) {
                return isoParsed.format('YYYY-MM-DDTHH:mm');
            }
        }
        return value.includes('T') ? value : '';
    }

    private fromInputDateTime(value: string): string {
        if (!value) return '';
        const moment = (window as any).moment;
        if (moment) {
            const parsed = moment(value);
            if (parsed.isValid()) {
                return parsed.format(DateTimes.defaultDateFormat);
            }
        }
        return value;
    }

    private isValidNumberInput(value: string): boolean {
        if (value.trim() === '') return false;
        return !isNaN(Number(value));
    }

    private isValidDateTimeInput(value: string, allowEmpty: boolean): boolean {
        if (value.trim() === '') return allowEmpty;
        const moment = (window as any).moment;
        if (moment) {
            const parsed = moment(value);
            return parsed.isValid();
        }
        return value.includes('T');
    }

    constructor(props: VariableEditDialogProps) {
        super(props);
        
        // Get current value based on variable type
        let currentValue = '';
        let arrayValues: string[] = [];
        if (props.variable.vartype === 'VT_NUMBER') {
            currentValue = String(props.variable.dNumType);
        } else if (props.variable.vartype === 'VT_STRING') {
            currentValue = props.variable.sString;
        } else if (props.variable.vartype === 'VT_DATETIME') {
            currentValue = this.toInputDateTime(props.variable.dtDateTime || '');
        } else if (props.variable.vartype === 'VT_NUMBERARRAY' && props.variable.VarArray) {
            arrayValues = props.variable.VarArray.map((value: any) => String(value));
        } else if (props.variable.vartype === 'VT_STRINGARRAY' && props.variable.VarArray) {
            arrayValues = props.variable.VarArray.map((value: any) => String(value));
        } else if (props.variable.vartype === 'VT_DATETIMEARRAY' && props.variable.VarArray) {
            arrayValues = props.variable.VarArray.map((value: any) => this.toInputDateTime(String(value)));
        }
        
        this.state = {
            value: currentValue,
            frozen: !!(cheatFreezes.variables[props.variable.varname]),
            arrayValues: arrayValues
        };
    }

    handleSave = (): void => {
        const vartype = this.props.variable.vartype;
        if (this.isArrayType(vartype)) {
            let payload = '';
            if (vartype === 'VT_NUMBERARRAY') {
                const parsed = this.state.arrayValues.map((value: string) => parseFloat(value));
                payload = JSON.stringify(parsed);
            } else if (vartype === 'VT_STRINGARRAY') {
                payload = JSON.stringify(this.state.arrayValues);
            } else if (vartype === 'VT_DATETIMEARRAY') {
                const parsed = this.state.arrayValues.map((value: string) => this.fromInputDateTime(value));
                payload = JSON.stringify(parsed);
            }
            this.props.onSave(payload, this.state.frozen);
            return;
        }

        if (vartype === 'VT_DATETIME') {
            const payload = this.fromInputDateTime(this.state.value);
            this.props.onSave(payload, this.state.frozen);
            return;
        }

        this.props.onSave(this.state.value, this.state.frozen);
    };

    handleCancel = (): void => {
        this.props.onCancel();
    };

    render(): any {
        const DialogComponent = (window as any).Dialog;
        if (!DialogComponent) return null;

        const varTypeLabel = this.props.variable.vartype === 'VT_NUMBER' ? 'Number' :
                           this.props.variable.vartype === 'VT_STRING' ? 'String' :
                           this.props.variable.vartype === 'VT_DATETIME' ? 'Date/Time' :
                           this.props.variable.vartype === 'VT_NUMBERARRAY' ? 'Number Array' :
                           this.props.variable.vartype === 'VT_STRINGARRAY' ? 'String Array' :
                           this.props.variable.vartype === 'VT_DATETIMEARRAY' ? 'Date/Time Array' :
                           this.props.variable.vartype;

        const vartype = this.props.variable.vartype;
        const isArray = this.isArrayType(vartype);

        let errorMessage = '';
        if (vartype === 'VT_NUMBER') {
            if (!this.isValidNumberInput(this.state.value)) {
                errorMessage = 'Enter a valid number.';
            }
        } else if (vartype === 'VT_DATETIME') {
            if (!this.isValidDateTimeInput(this.state.value, true)) {
                errorMessage = 'Enter a valid date/time.';
            }
        } else if (vartype === 'VT_NUMBERARRAY') {
            if (this.state.arrayValues.some((value: string) => !this.isValidNumberInput(value))) {
                errorMessage = 'All items must be valid numbers.';
            }
        } else if (vartype === 'VT_DATETIMEARRAY') {
            if (this.state.arrayValues.some((value: string) => !this.isValidDateTimeInput(value, false))) {
                errorMessage = 'All items must be valid date/times.';
            }
        }

        const isSaveDisabled = errorMessage.length > 0;

        return React.createElement(DialogComponent, {
            title: `Edit Variable: ${this.props.variable.varname}`,
            onClose: this.handleCancel,
            actionButtons: React.createElement(React.Fragment, null,
                React.createElement('button', {
                    className: 'btn',
                    onClick: this.handleCancel
                }, 'Cancel'),
                React.createElement('button', {
                    className: 'btn',
                    onClick: this.handleSave,
                    disabled: isSaveDisabled
                }, 'Save')
            ),
            closeOnBackdropClick: false
        },
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '16px' } },
                React.createElement('div', null,
                    React.createElement('strong', null, 'Type: '),
                    React.createElement('span', null, varTypeLabel)
                ),
                this.props.variable.VarComment && React.createElement('div', null,
                    React.createElement('strong', null, 'Description: '),
                    React.createElement('span', null, this.props.variable.VarComment)
                ),
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                    React.createElement('label', { style: { fontWeight: 'bold' } }, 'Value:'),
                    !isArray && vartype === 'VT_NUMBER' && React.createElement('input', {
                        type: 'number',
                        value: this.state.value,
                        onChange: (e: ChangeEvent<HTMLInputElement>) => {
                            this.setState({ value: e.target.value });
                        },
                        step: 'any',
                        style: {
                            width: '100%',
                            padding: '8px',
                            fontFamily: 'var(--font-game-text)',
                            fontSize: '14px',
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px'
                        }
                    }),
                    !isArray && vartype === 'VT_DATETIME' && React.createElement('input', {
                        type: 'datetime-local',
                        value: this.state.value,
                        onChange: (e: ChangeEvent<HTMLInputElement>) => {
                            this.setState({ value: e.target.value });
                        },
                        style: {
                            width: '100%',
                            padding: '8px',
                            fontFamily: 'var(--font-game-text)',
                            fontSize: '14px',
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px'
                        }
                    }),
                    !isArray && vartype === 'VT_STRING' && React.createElement('textarea', {
                        value: this.state.value,
                        onChange: (e: ChangeEvent<HTMLTextAreaElement>) => {
                            this.setState({ value: e.target.value });
                        },
                        rows: 4,
                        style: {
                            width: '100%',
                            padding: '8px',
                            fontFamily: 'var(--font-game-text)',
                            fontSize: '14px',
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px',
                            resize: 'vertical'
                        }
                    }),
                    isArray && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                        this.state.arrayValues.length === 0 && React.createElement('div', { style: { opacity: 0.7 } }, 'No items yet. Add one below.'),
                        this.state.arrayValues.map((value: string, index: number) => {
                            const inputType = vartype === 'VT_NUMBERARRAY' ? 'number' : (vartype === 'VT_DATETIMEARRAY' ? 'datetime-local' : 'text');
                            return React.createElement('div', { key: index, style: { display: 'flex', gap: '8px', alignItems: 'center' } },
                                React.createElement('span', { style: { width: '32px' } }, `${index}:`),
                                React.createElement('input', {
                                    type: inputType,
                                    value: value,
                                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                                        const updated = [...this.state.arrayValues];
                                        updated[index] = e.target.value;
                                        this.setState({ arrayValues: updated });
                                    },
                                    step: inputType === 'number' ? 'any' : undefined,
                                    style: {
                                        flex: 1,
                                        padding: '8px',
                                        fontFamily: 'var(--font-game-text)',
                                        fontSize: '14px',
                                        border: '1px solid rgba(0, 0, 0, 0.2)',
                                        borderRadius: '4px'
                                    }
                                }),
                                React.createElement('button', {
                                    className: 'btn',
                                    onClick: () => {
                                        const updated = this.state.arrayValues.filter((_: string, idx: number) => idx !== index);
                                        this.setState({ arrayValues: updated });
                                    }
                                }, 'Remove')
                            );
                        }),
                        React.createElement('button', {
                            className: 'btn',
                            onClick: () => {
                                this.setState({ arrayValues: [...this.state.arrayValues, ''] });
                            }
                        }, 'Add Item')
                    ),
                    errorMessage && React.createElement('div', { style: { color: '#d9534f', fontSize: '12px' } }, errorMessage)
                ),
                React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                    React.createElement('input', {
                        type: 'checkbox',
                        checked: this.state.frozen,
                        onChange: (e: ChangeEvent<HTMLInputElement>) => {
                            this.setState({ frozen: e.target.checked });
                        }
                    }),
                    React.createElement('span', null, 'Freeze variable (prevent changes)')
                )
            )
        );
    }
}

(window as any).VariableEditDialog = VariableEditDialog;
export default VariableEditDialog;
